import React, { useEffect, useMemo, useState } from "react";
import CompareModal from "./Components/CompareModal/CompareModal";
import styles from "./FacultyList.module.scss";
import FilterFaculty from "./Components/FiltersFaculty";
import useDeviceCheck from "Hooks/useDeviceCheck";
import KeywordClickSnackBar from "Explore/Components/KeywordClickSnackBar";
import TabListSelect from "Explore/Components/TabListSelect";
import QuickViewCard from "./Components/QuickViewCard/QuickViewCard";
import TableView from "./Components/TableView/TableView";
import {
  defaultBenchmarkFilter,
  indicatorList,
} from "Explore/Config/FilterConfig";
import { analyzeInstitution } from "Explore/Services/Benchmarking";
import { genArray } from "Explore/Utils/ArrayGenerator";
import { List, Skeleton } from "antd";

const VIEW_OPTIONS = [
  {
    label: (
      <>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
        <div>Quick View</div>
      </>
    ),
    value: 0,
  },
  {
    label: (
      <>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        <div>Table View</div>
      </>
    ),
    value: 1,
  },
];

const initialResult = {
  total: 0,
  page: 0,
  page_size: defaultBenchmarkFilter.page_size,
  data: [],
};

const Benchmarking = () => {
  const { isSmallDesktop } = useDeviceCheck();
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [activeView, setActiveView] = useState(VIEW_OPTIONS[0].value);
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [benchmarkResult, setBenchmarkResult] = useState(initialResult);
  const [benchmarkFilter, setBenchmarkFilter] = useState(defaultBenchmarkFilter);

  useEffect(() => {
    setSelectedInstitutions([]);
  }, [activeView]);

  useEffect(() => {
    setSelectedInstitutions((prev) =>
      prev.filter((item) =>
        benchmarkResult.data.some(
          (institution) => institution.institution_id === item.institution_id
        )
      )
    );
  }, [benchmarkResult.data]);

  const toggleSelection = (inst) => {
    setSelectedInstitutions((prev) => {
      const isSelected = prev.some((i) => i.institution_id === inst.institution_id);
      if (isSelected) {
        return prev.filter((i) => i.institution_id !== inst.institution_id);
      }
      return [...prev, inst];
    });
  };

  const updateBenchmarkFilter = (nextPartial) => {
    setBenchmarkFilter((prev) => {
      const merged = { ...prev, ...nextPartial };
      const shouldKeepPage = Object.keys(nextPartial).every((key) =>
        ["page", "page_size"].includes(key)
      );
      if (!shouldKeepPage) {
        merged.page = 0;
      }

      if (!merged.indicators?.length) {
        merged.indicators = [indicatorList[0].value];
      }
      if (!merged.indicators.includes(merged.order_by)) {
        merged.order_by = merged.indicators[0];
      }

      return merged;
    });
  };

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);

    analyzeInstitution({
      filters: {
        location: benchmarkFilter.location,
        time_span: benchmarkFilter.time_span,
      },
      indicators: benchmarkFilter.indicators,
      order_by: benchmarkFilter.order_by,
      order_direction: benchmarkFilter.order_direction,
      page: benchmarkFilter.page,
      page_size: benchmarkFilter.page_size,
    })
      .then((res) => {
        if (!isMounted) return;
        setBenchmarkResult({
          total: res?.total || 0,
          page: res?.page || 0,
          page_size: res?.page_size || benchmarkFilter.page_size,
          data: res?.data || [],
        });
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err);
        setBenchmarkResult(initialResult);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [benchmarkFilter]);

  const maxCitations = useMemo(() => {
    if (!benchmarkResult.data.length) {
      return 0;
    }
    return Math.max(
      ...benchmarkResult.data.map(
        (d) => d?.indicators?.total_citations || 0
      )
    );
  }, [benchmarkResult.data]);

  return (
    <>
      <title>Benchmark the Institution by Common Statistic | Academic Explorer</title>
      <meta
        name="description"
        content="Benchmark the institution by common statistic with filters by time span, state, indicators, and sorting."
      />
      <div className={styles["faculty-list"]}>
        {!isSmallDesktop && (
          <FilterFaculty
            filter={benchmarkFilter}
            updateFilter={updateBenchmarkFilter}
          />
        )}
        <div className={styles["results"]}>
          <div className={styles["results__header"]}>
            <h2 className={styles["results--title"]}>
              Benchmark the institution by common statistic
            </h2>
            <p className={styles["results--description"]}>
              Compare institution performance with backend-powered indicators.
            </p>
          </div>

          <div className={styles["results--controller"]}>
            <div className={styles["results__header-controller"]}>
              <TabListSelect
                options={VIEW_OPTIONS}
                currentOption={activeView}
                setOption={setActiveView}
              />
              <button
                className={styles["compare-btn"]}
                disabled={selectedInstitutions.length === 0}
                onClick={() => setIsCompareModalOpen(true)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M18 20V10M12 20V4M6 20v-6" />
                </svg>
                Compare with chart{" "}
                {selectedInstitutions.length > 0 &&
                  `(${selectedInstitutions.length})`}
              </button>
            </div>
          </div>

          <div className={styles["results__body"]}>
            {isLoading ? (
              <List
                grid={{ gutter: 25, column: 1 }}
                dataSource={genArray(55)}
                renderItem={(index) => {
                  return (
                    <List.Item key={index}>
                      <div
                        className={styles["loading-card"]}
                      >
                        <Skeleton
                          paragraph={{
                            rows: 2,
                          }}
                          active
                        />
                      </div>
                    </List.Item>
                  );
                }}
              />
            ) : error ? (
              <div>Unable to load benchmark data. Please try again.</div>
            ) : !benchmarkResult.data.length ? (
              <div>No institution found for current filters.</div>
            ) : activeView === VIEW_OPTIONS[0].value ? (
              benchmarkResult.data.map((item, idx) => (
                <QuickViewCard
                  key={item.institution_id}
                  data={item}
                  rank={idx + 1 + benchmarkResult.page * benchmarkResult.page_size}
                  maxCitations={maxCitations}
                  selectedIndicators={benchmarkFilter.indicators}
                  isSelected={selectedInstitutions.some(
                    (i) => i.institution_id === item.institution_id
                  )}
                  onToggle={() => toggleSelection(item)}
                />
              ))
            ) : (
              <TableView
                data={benchmarkResult.data}
                selectedIndicators={benchmarkFilter.indicators}
                selectedInstitutions={selectedInstitutions}
                onToggle={toggleSelection}
              />
            )}
          </div>
        </div>
      </div>
      <KeywordClickSnackBar open={snackBarOpen} setOpen={setSnackBarOpen} />
      <CompareModal
        isOpen={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
        selectedData={selectedInstitutions}
        selectedIndicators={benchmarkFilter.indicators}
      />
    </>
  );
};

export default Benchmarking;
